// SoilAnalysisDrawer.tsx
import React, { useEffect, useRef, useState } from "react";
import {
  Collapse,
  Flex,
  Typography,
  Select,
  Button,
  Spin,
  message,
} from "antd";
import axios from "axios";
import useAxios from "../custom-hooks/useAxios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "./SoilAnalysisDrawer.less";
import { useNewTrialState } from "../../pages/new-trail/hooks/NewTrialStateProvider";
import {
  CloseOutlined,
  DownOutlined,
  InfoCircleOutlined,
  UpOutlined,
} from "@ant-design/icons";
import type { Polygon } from "@turf/turf";
import { sensitiveEnv } from "../../config/sensitiveEnv";
import { getAccessToken } from "../utils/axios";
import { SoilAnalysisChart } from "./SoilAnalysisChart";
import { COLOR_PALETTES } from "./soilColorUtil";
import { ZoneColorSlider } from "./CustomThresholdSlider";
import { Loader } from "../components/Loader";
import { useTrialsRouter } from "../custom-hooks/useTrialsRouter";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const { Text } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

interface ColorCode {
  value_range: string;
  color_code: string;
}

interface SoilProperty {
  id: string;
  name: string;
  type: string;
  code: string;
  uom: string | null;
  property_type: string;
  color_codes: ColorCode[];
  raster_url?: string;
  bbox?: number[][];
}

interface SoilTest {
  id: string;
  title: string;
  created: string;
  providers: Array<{
    provider_name: string;
    id: string;
  }>;

  layer_properties: SoilProperty[];
}

interface Collection {
  id: string;
  title: string;
  description: string;
  created: string;
  links?: Array<{
    id: string;
    href: string;
    rel: string;
    type: string;
    soil_property_id: string | null;
    soil_property_name: string | null;
  }>;
}

type CollectionLink = NonNullable<Collection["links"]>[number];

interface ChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    backgroundColor: string[];
    borderColor: string[];
    borderWidth: number;
    borderRadius: number;
  }>;
}

interface CollectionsResponse {
  collections: Collection[];
  pagination: {
    has_next_page: boolean;
    page: number;
    total_pages: number;
  };
}

interface ZoneData {
  min: number;
  max: number;
  color: string;
  percentage: number;
  count: number;
}

interface RasterStats {
  min: number;
  max: number;
  mean: number;
  stdDev: number;
}

interface GeoJSONFeature {
  type: string;
  properties: {
    value?: number;
    [key: string]: any;
  };
  geometry: any;
}

interface GeoJSONData {
  type: string;
  features: GeoJSONFeature[];
  geometry?: Polygon;
}

interface SoilAnalysisDrawerProps {
  onSelectImage: (image: any) => void;
  onCloseDrawer?: () => void;
}

const normalizeSoilPropertyName = (value?: string | null): string =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const SoilAnalysisDrawer: React.FC<SoilAnalysisDrawerProps> = ({
  onSelectImage,
  onCloseDrawer,
}) => {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [collectionsPage, setCollectionsPage] = useState(1);
  const [hasNextCollectionsPage, setHasNextCollectionsPage] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<SoilTest | null>(
    null
  );
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [loadingMoreCollections, setLoadingMoreCollections] = useState(false);
  const [loadingCollectionData, setLoadingCollectionData] = useState(false);
  const [expandedCollections, setExpandedCollections] = useState<string[]>([]);
  const [expandedProperties, setExpandedProperties] = useState<string[]>([]);
  const { trialInfo, setSoilOperationsData, soilColorPalette, soilOpacity } =
    useNewTrialState();

  const [chartConfigs, setChartConfigs] = useState<
    Record<
      string,
      {
        zones: number;
        palette: string;
        chartData: ChartData | null;
        zoneData: ZoneData[];
        distributionZoneData: ZoneData[];
        distributionStats: RasterStats | null;
        stats: RasterStats | null;
        loading: boolean;
        geoJsonData?: GeoJSONData;
        pendingZones?: number;
        pendingPalette?: string;
        pendingThresholds?: number[];
        thresholds?: number[]; // custom numeric thresholds used to define zone boundaries
      }
    >
  >({});
  const chartConfigsRef = useRef(chartConfigs);

  useEffect(() => {
    chartConfigsRef.current = chartConfigs;
  }, [chartConfigs]);

  const normalizePaletteKey = (
    palette: string = "default"
  ): keyof typeof COLOR_PALETTES => {
    const normalized = palette.toUpperCase();
    if (normalized === "ACCESSIBILITY") return "ACCESSIBILITY";
    if (normalized === "NDVI") return "NDVI";
    return "DEFAULT";
  };

  const [pendingChanges, setPendingChanges] = useState<Record<string, boolean>>(
    {}
  );
  const loadingNextPageRef = useRef(false);
  const isSelected = trialInfo?.field_ids?.[0];
  const SOIL_MAP_PROVIDERS = "SoilOptix,SoilLabs,Veris";
  const SOIL_API_KEY = sensitiveEnv.soilApiKey;

  // Use proxy in development, direct URL in production
  const soilHubBaseUrl = process.env.REACT_APP_SOILHUB_BASE_URL ?? "";
  const collectionsPerPage = 15;

  const buildCollectionsListApiUrl = (page: number): string =>
    `${soilHubBaseUrl}/api/v1/collections/?page=${String(page)}&limit=${String(
      collectionsPerPage
    )}&field_id=${String(isSelected)}&providers=${String(SOIL_MAP_PROVIDERS)}`;
  const GeoJsonUrl = `${soilHubBaseUrl}/api/v1/map/geojson`;
  const ZoneConverterUrl =
    process.env.REACT_APP_POINTS_TO_MULTIPOLYGON_API ??
    "https://stage.service-springboot-utility-service.api.seedselector.cropwise.com/geojson/point-to-multipolygon-converter";

  const [isLoading, setIsLoading] = useState(false);

  const resolvePropertyMeasure = (
    collection: Collection | undefined,
    property: Pick<SoilProperty, "id" | "name" | "raster_url">
  ): string => {
    const link = findPropertyLink(collection, property);

    if (link?.soil_property_name) return link.soil_property_name;

    // Extract the measure param from any available href, resolving relative
    // URLs against soilHubBaseUrl so new URL() doesn't throw.
    const hrefs = [link?.href, property.raster_url].filter((h): h is string =>
      Boolean(h)
    );
    for (const href of hrefs) {
      try {
        const url = new URL(href, soilHubBaseUrl);
        const measure = url.searchParams.get("measure");
        if (measure) return measure;
      } catch {
        // not parseable — try next
      }
    }

    return property.name;
  };

  const findPropertyLink = (
    collection: Collection | undefined,
    property: Pick<SoilProperty, "id" | "name">
  ): CollectionLink | undefined => {
    const normalizedPropertyName = normalizeSoilPropertyName(property.name);

    return collection?.links?.find((link) => {
      if (link.soil_property_id === property.id) {
        return true;
      }

      return (
        normalizedPropertyName.length > 0 &&
        normalizeSoilPropertyName(link.soil_property_name) ===
          normalizedPropertyName
      );
    });
  };

  const createImageData = (
    property: SoilProperty,
    zoneData: ZoneData[],
    stats: RasterStats,
    geoJsonData: GeoJSONData,
    zones: number,
    palette: string,
    opacity: number
  ): {
    image_location: string;
    bbox: number[][];
    property: SoilProperty;
    zoneData: ZoneData[];
    stats: RasterStats;
    geoJsonData: GeoJSONData;
    zones: number;
    palette: string;
    opacity: number;
  } => ({
    image_location: property.raster_url ?? "",
    bbox: property.bbox ?? [],
    property,
    zoneData,
    stats,
    geoJsonData,
    zones,
    palette,
    opacity,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchCollections = async (
      page: number,
      append: boolean
    ): Promise<void> => {
      if (!isSelected || !soilHubBaseUrl) return;

      if (append) {
        setLoadingMoreCollections(true);
      } else {
        setLoadingCollections(true);
      }

      try {
        const response = await axios.get<CollectionsResponse>(
          buildCollectionsListApiUrl(page),
          {
            headers: {
              Authorization: SOIL_API_KEY,
              "X-API-Key": SOIL_API_KEY,
            },
          }
        );

        if (!isMounted) return;

        // Filter out collections with empty or null titles
        const validCollections = (response.data?.collections ?? []).filter(
          (collection) => collection?.title?.trim()
        );
        const pagination = response.data?.pagination;

        setCollectionsPage(pagination?.page ?? page);
        setHasNextCollectionsPage(
          pagination?.has_next_page ??
            (pagination?.total_pages != null
              ? (pagination.page ?? page) < pagination.total_pages
              : false)
        );

        if (append) {
          setCollections((prev) => {
            const merged = [...prev, ...validCollections];
            const uniqueById = new Map(merged.map((item) => [item.id, item]));
            return Array.from(uniqueById.values());
          });
        } else {
          setCollections(validCollections);
        }
      } catch (error) {
        if (!isMounted) return;

        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        console.error("Error fetching collections:", errorMessage, error);
        if (!append) {
          void message.error("Failed to fetch soil collections");
        }
      } finally {
        if (isMounted) {
          if (append) {
            setLoadingMoreCollections(false);
          } else {
            setLoadingCollections(false);
          }
          loadingNextPageRef.current = false;
        }
      }
    };

    setCollections([]);
    setCollectionsPage(1);
    setHasNextCollectionsPage(false);
    setExpandedCollections([]);
    setExpandedProperties([]);
    setSelectedCollection(null);
    void fetchCollections(1, false);

    return () => {
      isMounted = false;
    };
  }, [isSelected, soilHubBaseUrl]);

  const loadNextCollectionsPage = async (): Promise<void> => {
    if (
      !hasNextCollectionsPage ||
      loadingCollections ||
      loadingMoreCollections ||
      loadingNextPageRef.current
    ) {
      return;
    }

    loadingNextPageRef.current = true;
    setLoadingMoreCollections(true);
    const nextPage = collectionsPage + 1;

    try {
      const response = await axios.get<CollectionsResponse>(
        buildCollectionsListApiUrl(nextPage),
        {
          headers: {
            Authorization: SOIL_API_KEY,
            "X-API-Key": SOIL_API_KEY,
          },
        }
      );

      const validCollections = (response.data?.collections ?? []).filter(
        (collection) => collection?.title?.trim()
      );
      const pagination = response.data?.pagination;

      setCollections((prev) => {
        const merged = [...prev, ...validCollections];
        const uniqueById = new Map(merged.map((item) => [item.id, item]));
        return Array.from(uniqueById.values());
      });
      setCollectionsPage(pagination?.page ?? nextPage);
      setHasNextCollectionsPage(
        pagination?.has_next_page ??
          (pagination?.total_pages != null
            ? (pagination.page ?? nextPage) < pagination.total_pages
            : false)
      );
    } catch (error) {
      console.error("Error fetching next collections page:", error);
    } finally {
      setLoadingMoreCollections(false);
      loadingNextPageRef.current = false;
    }
  };

  const handleCollectionsScroll = (
    event: React.UIEvent<HTMLDivElement>
  ): void => {
    const target = event.currentTarget;
    const nearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 80;

    if (nearBottom) {
      void loadNextCollectionsPage();
    }
  };

  const fetchGeoJsonData = async (
    collectionId: string,
    providerName: string,
    propertyName: string
  ): Promise<GeoJSONData> => {
    try {
      const response = await axios.get<GeoJSONData>(
        `${GeoJsonUrl}/?collection_id=${String(collectionId)}&measure=${String(
          propertyName
        ).toLowerCase()}&provider=${providerName}`,
        {
          headers: {
            Authorization: SOIL_API_KEY,
            "X-API-Key": SOIL_API_KEY,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching GeoJSON data:", error);
      throw new Error(
        `Failed to fetch GeoJSON data for ${String(propertyName)}`
      );
    }
  };

  const { farmId } = useTrialsRouter();
  const { response: farmProperties } = useAxios(
    `/properties/${String(farmId)}/fields?attributes=geometry`,
    true
  );

  const convertToZones = async (
    geoJsonData: GeoJSONData,
    numberOfZones: number,
    thresholds?: number[]
  ): Promise<GeoJSONData> => {
    try {
      const values = geoJsonData.features
        .map((feature) => feature.properties?.value)
        .filter((value) => value !== null && value !== undefined);

      if (values.length === 0) {
        throw new Error("No valid values found in GeoJSON features");
      }

      const filteredValues = values.filter(
        (v): v is number => typeof v === "number"
      );
      const overallMin =
        filteredValues.length > 0 ? Math.min(...filteredValues) : 0;
      const overallMax =
        filteredValues.length > 0 ? Math.max(...filteredValues) : 0;

      const zoneThresholdAxis =
        thresholds && thresholds.length > 1
          ? [...thresholds].sort((a, b) => a - b)
          : Array.from({ length: numberOfZones + 1 }, (_, i) => {
              const minNum = Number(overallMin);
              const maxNum = Number(overallMax);
              return minNum + (i * (maxNum - minNum)) / numberOfZones;
            });

      const getZoneGroups = (
        ranges: number[]
      ): Array<{ min: number; max: number }> => {
        const groups = [];
        for (let i = 0; i < ranges.length - 1; i++) {
          groups.push({
            min: ranges[i],
            max: ranges[i + 1],
          });
        }
        return groups;
      };

      const response = await axios.post<GeoJSONData>(
        ZoneConverterUrl,
        {
          type: "FeatureCollection",
          features: geoJsonData.features,
          field_border: farmProperties?.content?.[0]?.geometry?.coordinates,
          groups: getZoneGroups(zoneThresholdAxis),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAccessToken()}`,
            Api_consumer: "PLANTING",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error converting to zones:", error);
      throw new Error("Failed to convert points to zones");
    }
  };

  const generateZoneDataFromGeoJson = (
    geoJsonData: GeoJSONData,
    numberOfZones: number,
    palette: string = "default"
  ): { zoneData: ZoneData[]; stats: RasterStats } => {
    const values = geoJsonData.features
      .map((feature) => feature.properties?.value)
      .filter((value) => value !== null && value !== undefined);

    if (values.length === 0) {
      return generateFallbackZoneData(numberOfZones);
    }

    const min: number = Math.min(
      ...values.filter((v): v is number => typeof v === "number")
    );
    const safeMin = Number.isFinite(min) ? min : 0;
    const maxRaw = Math.max(
      ...values.filter((v): v is number => typeof v === "number")
    );
    const safeMax = Number.isFinite(maxRaw) ? maxRaw : 0;
    const filteredValues = values.filter(
      (v): v is number => typeof v === "number"
    );
    const mean =
      filteredValues.length > 0
        ? filteredValues.reduce((sum, val) => sum + val, 0) /
          filteredValues.length
        : 0;
    const stdDev = Math.sqrt(
      filteredValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
        (filteredValues.length || 1)
    );

    const zoneData: ZoneData[] = [];
    const interval = Number(safeMax - safeMin) / Number(numberOfZones);
    const paletteColors = COLOR_PALETTES[normalizePaletteKey(palette)];

    for (let i = 0; i < numberOfZones; i++) {
      const zoneMin = safeMin + i * interval;
      const zoneMax = safeMin + (i + 1) * interval;

      const valuesInZone = values.filter(
        (value): value is number =>
          typeof value === "number" && value >= zoneMin && value <= zoneMax
      );
      const count = valuesInZone.length;
      const percentage = Math.round((count / values.length) * 100);

      zoneData.push({
        min: zoneMin,
        max: zoneMax,
        color: paletteColors[i % paletteColors.length],
        percentage,
        count,
      });
    }

    return {
      zoneData,
      stats: {
        min: safeMin,
        max: safeMax,
        mean,
        stdDev,
      },
    };
  };

  const generateFallbackZoneData = (
    numberOfZones: number
  ): { zoneData: ZoneData[]; stats: RasterStats } => {
    const zoneData: ZoneData[] = [];
    const paletteColors = COLOR_PALETTES.DEFAULT;

    // Create more realistic fallback data
    for (let i = 0; i < numberOfZones; i++) {
      const percentage = Math.round((100 / numberOfZones) * (1 - i * 0.1)); // Gradually decreasing
      const count = percentage * 10;

      zoneData.push({
        min: i * (100 / numberOfZones),
        max: (i + 1) * (100 / numberOfZones),
        color: paletteColors[i % paletteColors.length],
        percentage,
        count,
      });
    }

    // Normalize percentages to sum to 100
    const totalPercentage = zoneData.reduce(
      (sum, zone) => sum + zone.percentage,
      0
    );
    zoneData.forEach((zone) => {
      zone.percentage = Math.round((zone.percentage / totalPercentage) * 100);
    });

    return {
      zoneData,
      stats: { min: 0, max: 100, mean: 50, stdDev: 25 },
    };
  };

  const sanitizeThresholds = (
    rawThresholds: number[],
    min: number,
    max: number,
    zones: number
  ): number[] => {
    const expectedLength = zones + 1;
    if (
      !Number.isFinite(min) ||
      !Number.isFinite(max) ||
      max <= min ||
      zones < 1
    ) {
      return [];
    }

    const stepFloor = Math.max((max - min) / (expectedLength * 1000), 1e-6);
    const sorted = rawThresholds
      .filter((value): value is number => Number.isFinite(value))
      .sort((a, b) => a - b);

    let normalized: number[];
    if (sorted.length !== expectedLength) {
      const interval = (max - min) / zones;
      normalized = Array.from(
        { length: expectedLength },
        (_, i) => min + i * interval
      );
    } else {
      normalized = [...sorted];
    }

    normalized[0] = min;
    normalized[normalized.length - 1] = max;

    for (let i = 1; i < normalized.length - 1; i++) {
      const minAllowed = normalized[i - 1] + stepFloor;
      const maxAllowed = max - stepFloor * (normalized.length - 1 - i);
      normalized[i] = Math.min(Math.max(normalized[i], minAllowed), maxAllowed);
    }

    return normalized.map((value) => Number.parseFloat(value.toFixed(6)));
  };

  const generateChartData = (
    property: SoilProperty,
    zoneData: ZoneData[],
    maxBars: number = 10
  ): ChartData => {
    if (zoneData.length < 11) {
      const detailedZoneData = [...zoneData];
      const paletteColors = COLOR_PALETTES.DEFAULT;

      for (let i = zoneData.length; i < 11; i++) {
        const percentage =
          i < zoneData.length
            ? zoneData[i].percentage / Math.ceil(zoneData.length / 11)
            : 0;

        detailedZoneData.push({
          min: zoneData[0]?.min || 0 + i * 10,
          max: zoneData[0]?.max || 0 + (i + 1) * 10,
          color: paletteColors[i % paletteColors.length],
          percentage: Math.round(percentage),
          count: Math.round(percentage * 10),
        });
      }

      return {
        labels: detailedZoneData.map(
          (zone) =>
            `${zone.min.toFixed(2)} - ${zone.max.toFixed(2)} ${
              property.uom ?? ""
            }`
        ),
        datasets: [
          {
            label: `${property.name} Distribution`,
            data: detailedZoneData.map((zone) => zone.percentage),
            backgroundColor: detailedZoneData.map((zone) => zone.color),
            borderColor: detailedZoneData.map((zone) => zone.color),
            borderWidth: 1,
            borderRadius: 4,
          },
        ],
      };
    }

    return {
      labels: zoneData.map(
        (zone) =>
          `${zone.min.toFixed(2)} - ${zone.max.toFixed(2)} ${
            property.uom ?? ""
          }`
      ),
      datasets: [
        {
          label: `${property.name} Distribution`,
          data: zoneData.map((zone) => zone.percentage),
          backgroundColor: zoneData.map((zone) => zone.color),
          borderColor: zoneData.map((zone) => zone.color),
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  };

  const handleApplyChanges = async (propertyId: string): Promise<void> => {
    const property = selectedCollection?.layer_properties.find(
      (p) => p.id === propertyId
    );
    if (!property || !selectedCollection) return;

    try {
      const config =
        chartConfigsRef.current[propertyId] ?? chartConfigs[propertyId];
      if (!config) return;
      const newZones = config.pendingZones ?? config.zones;
      const newPalette = config.pendingPalette ?? config.palette;

      // Validate provider availability before making request
      const provider = selectedCollection.providers?.[0]?.provider_name;
      if (!provider) {
        void message.error("No data provider available for this collection");
        return;
      }

      // Resolve the API-canonical measure name from the collection link so that
      // short codes stored in property.name (e.g. "B" for Boron) don't produce
      // a broken measure= query parameter.
      const applyCollection = collections.find(
        (c) => c.id === selectedCollection.id
      );
      const applyMeasure = resolvePropertyMeasure(applyCollection, property);

      setIsLoading(true);
      const geoJsonData = await fetchGeoJsonData(
        selectedCollection.id,
        provider,
        applyMeasure
      );

      const values = geoJsonData.features
        .map((feature) => feature.properties?.value)
        .filter((value): value is number => typeof value === "number");

      const dataMin = values.length > 0 ? Math.min(...values) : 0;
      const dataMax = values.length > 0 ? Math.max(...values) : 0;

      const activeThresholds =
        config.pendingThresholds ?? config.thresholds ?? [];

      // Proportionally remap thresholds from the old stats range to the fresh
      // data range.  This prevents handles collapsing when the first Apply goes
      // from fallback (0-100) to the real data range (e.g. 5-48).
      const oldMin = config.distributionStats?.min ?? dataMin;
      const oldMax = config.distributionStats?.max ?? dataMax;
      const rangeChanged =
        Number.isFinite(oldMin) &&
        Number.isFinite(oldMax) &&
        oldMax > oldMin &&
        dataMax > dataMin &&
        (Math.abs(oldMin - dataMin) > 1e-6 ||
          Math.abs(oldMax - dataMax) > 1e-6);

      const remappedThresholds =
        rangeChanged && activeThresholds.length >= 2
          ? activeThresholds.map((t, i) => {
              if (i === 0) return dataMin;
              if (i === activeThresholds.length - 1) return dataMax;
              const proportion = Math.max(
                0,
                Math.min(1, (t - oldMin) / (oldMax - oldMin))
              );
              return dataMin + proportion * (dataMax - dataMin);
            })
          : activeThresholds;

      const validThresholds = sanitizeThresholds(
        remappedThresholds,
        dataMin,
        dataMax,
        newZones
      );

      const zonesData = await convertToZones(
        geoJsonData,
        newZones,
        validThresholds.length > 1 ? validThresholds : undefined
      );

      const { zoneData: distributionZoneData, stats: distributionStats } =
        validThresholds.length > 1
          ? generateZoneDataFromThresholds(
              geoJsonData,
              validThresholds,
              newPalette
            )
          : generateZoneDataFromGeoJson(geoJsonData, newZones, newPalette);

      const { zoneData: chartZoneData, stats: chartStats } =
        generateZoneDataFromGeoJson(geoJsonData, 10, newPalette);

      const chartData = generateChartData(property, chartZoneData);
      // Update the config
      setChartConfigs((prev) => ({
        ...prev,
        [propertyId]: {
          ...prev[propertyId],
          zones: newZones,
          palette: newPalette,
          zoneData: chartZoneData,
          stats: chartStats,
          distributionZoneData,
          distributionStats,
          chartData,
          geoJsonData: zonesData,
          pendingZones: undefined,
          pendingPalette: undefined,
          pendingThresholds: undefined,
          thresholds:
            validThresholds.length > 1
              ? validThresholds
              : sanitizeThresholds(
                  [
                    distributionStats.min,
                    ...distributionZoneData.map((zone) => zone.max),
                  ],
                  distributionStats.min,
                  distributionStats.max,
                  newZones
                ),
        },
      }));

      // Update map with correct zone data
      const imageData = createImageData(
        property,
        distributionZoneData,
        distributionStats,
        zonesData,
        newZones,
        newPalette,
        soilOpacity
      );

      setIsLoading(false);
      setSoilOperationsData(imageData);
      onSelectImage(imageData);

      setPendingChanges((prev) => ({ ...prev, [propertyId]: false }));
      void message.success(`Zones updated for ${property.name}`);
    } catch (error) {
      console.error("Error applying changes:", error);
      void message.error(`Failed to update zones for ${property.name}`);
    } finally {
      setIsLoading(false);
    }
  };

  const generateZoneDataFromThresholds = (
    geoJsonData: GeoJSONData,
    thresholds: number[],
    palette: string = "default"
  ): { zoneData: ZoneData[]; stats: RasterStats } => {
    const values = geoJsonData.features
      .map((feature) => feature.properties?.value)
      .filter((value): value is number => typeof value === "number");

    if (values.length === 0) {
      return generateFallbackZoneData(thresholds.length - 1);
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const stdDev = Math.sqrt(
      values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
        values.length
    );

    const zoneData: ZoneData[] = [];
    const paletteColors = COLOR_PALETTES[normalizePaletteKey(palette)];

    for (let i = 0; i < thresholds.length - 1; i++) {
      const zoneMin = thresholds[i];
      const zoneMax = thresholds[i + 1];
      const valuesInZone = values.filter(
        (value) => value >= zoneMin && value <= zoneMax
      );
      const count = valuesInZone.length;
      const percentage = Math.round((count / values.length) * 100);

      zoneData.push({
        min: zoneMin,
        max: zoneMax,
        color: paletteColors[i % paletteColors.length],
        percentage,
        count,
      });
    }

    return {
      zoneData,
      stats: {
        min,
        max,
        mean,
        stdDev,
      },
    };
  };

  const fetchCollectionData = async (collectionId: string): Promise<void> => {
    setLoadingCollectionData(true);
    try {
      const response = await axios.get<SoilTest>(
        `${soilHubBaseUrl}/api/v1/collections/${collectionId}`
      );
      const collection = collections.find((c) => c.id === collectionId);

      const propertiesWithRaster =
        response?.data?.layer_properties?.filter((prop) => {
          const propertyLink = findPropertyLink(collection, prop);
          const hasValidPropertyType = prop.property_type != null;
          return hasValidPropertyType && Boolean(propertyLink);
        }) || [];

      const enhancedProperties = propertiesWithRaster.map((prop) => {
        const propertyLink = findPropertyLink(collection, prop);
        return {
          ...prop,
          raster_url: propertyLink?.href ?? "",
          bbox: [
            [-180, -90],
            [180, -90],
            [180, 90],
            [-180, 90],
          ],
        };
      });

      setSelectedCollection({
        ...response?.data,
        layer_properties: enhancedProperties,
      });

      const initialConfigs: Record<string, any> = {};

      // Log each property"s details
      for (const prop of enhancedProperties) {
        const { zoneData: chartZoneData, stats: chartStats } =
          generateFallbackZoneData(10);
        const { zoneData: distributionZoneData, stats: distributionStats } =
          generateFallbackZoneData(4);

        const thresholds =
          distributionZoneData && distributionZoneData.length > 0
            ? [
                distributionStats.min,
                ...distributionZoneData.map((zone) => zone.max),
              ].sort((a, b) => a - b)
            : [0, 20, 40, 60, 80, 100];

        initialConfigs[prop.id] = {
          zones: 4,
          palette: "default",
          zoneData: chartZoneData,
          stats: chartStats,
          distributionZoneData: distributionZoneData || [],
          distributionStats: distributionStats || {
            min: 0,
            max: 100,
            mean: 50,
            stdDev: 25,
          },
          chartData: generateChartData(prop, chartZoneData),
          loading: false,
          thresholds,
        };
      }

      setChartConfigs(initialConfigs);
    } catch (error) {
      console.error("Error fetching collection data:", error);
      void message.error("Failed to fetch collection data");
      setSelectedCollection(null);
    } finally {
      setLoadingCollectionData(false);
    }
  };

  const handlePropertySelection = async (
    property: SoilProperty
  ): Promise<void> => {
    if (!selectedCollection) return;

    try {
      // PRESERVE existing thresholds while loading
      const currentConfig = chartConfigs[property.id] || {};
      const existingThresholds = currentConfig.thresholds;

      setChartConfigs((prev) => ({
        ...prev,
        [property.id]: {
          ...prev[property.id],
          loading: true,
          thresholds: existingThresholds, // Keep existing thresholds
        },
      }));

      const geoJsonData = await fetchGeoJsonData(
        selectedCollection?.id,
        selectedCollection?.providers?.[0]?.provider_name,
        property?.name
      );
      const zonesData = await convertToZones(geoJsonData, 4);

      const { zoneData: distributionZoneData, stats: distributionStats } =
        generateZoneDataFromGeoJson(geoJsonData, 4, "default");

      const { zoneData: chartZoneData, stats: chartStats } =
        generateZoneDataFromGeoJson(geoJsonData, 10, "default");

      const chartData = generateChartData(property, chartZoneData);

      // Generate proper thresholds for the new data
      const newThresholds = [
        distributionStats.min,
        ...distributionZoneData.map((zone) => zone.max),
      ].sort((a, b) => a - b);

      setChartConfigs((prev) => ({
        ...prev,
        [property.id]: {
          ...prev[property.id],
          zones: 4,
          palette: "default",
          zoneData: chartZoneData,
          stats: chartStats,
          distributionZoneData,
          distributionStats,
          chartData,
          geoJsonData: zonesData,
          loading: false,
          thresholds: newThresholds,
        },
      }));

      // ... your map update code ...
      const imageData = createImageData(
        property,
        distributionZoneData,
        distributionStats,
        zonesData,
        4,
        soilColorPalette,
        soilOpacity
      );

      setSoilOperationsData(imageData);
      onSelectImage(imageData);
      void message.success(`${property.name} data loaded successfully`);
    } catch (error) {
      console.error("Error loading property data:", error);
      void message.error(`Failed to load data for ${property.name}`);
    }
  };

  const handleCollectionPanelChange = (keys: string[] | string): void => {
    const expandedKeys = Array.isArray(keys) ? keys : [keys];
    setExpandedCollections(expandedKeys);
    setExpandedProperties([]);

    if (expandedKeys.length > 0) {
      const collectionId = expandedKeys[0];
      void fetchCollectionData(collectionId);
    } else {
      setSelectedCollection(null);
    }
  };

  const handlePropertyPanelChange = (keys: string[] | string): void => {
    const expandedKeys = Array.isArray(keys) ? keys : [keys];
    setExpandedProperties(expandedKeys);
  };

  const handlePropertyHeaderClick = (
    property: SoilProperty,
    config: { chartData?: ChartData | null },
    togglePanel: boolean
  ): void => {
    if (togglePanel && config.chartData) {
      handlePropertyPanelChange(
        expandedProperties.includes(property.id) ? [] : [property.id]
      );
    }

    void handlePropertySelection(property);
  };

  const handleZonesChange = (propertyId: string, value: number): void => {
    const config =
      chartConfigsRef.current[propertyId] ?? chartConfigs[propertyId];
    if (!config?.distributionStats) return;

    // Calculate new thresholds for the slider only
    const min = config.distributionStats.min;
    const max = config.distributionStats.max;
    const thresholds = sanitizeThresholds([], min, max, value);

    setChartConfigs((prev) => {
      const next = {
        ...prev,
        [propertyId]: {
          ...prev[propertyId],
          pendingZones: value,
          pendingThresholds: thresholds,
          thresholds,
        },
      };
      chartConfigsRef.current = next;
      return next;
    });
    setPendingChanges((prev) => ({ ...prev, [propertyId]: true }));
  };

  const handlePaletteChange = (propertyId: string, value: string): void => {
    setChartConfigs((prev) => {
      const next = {
        ...prev,
        [propertyId]: {
          ...prev[propertyId],
          pendingPalette: value,
        },
      };
      chartConfigsRef.current = next;
      return next;
    });
    setPendingChanges((prev) => ({ ...prev, [propertyId]: true }));
  };

  const trimCollectionTitle = (title: string): string => {
    // Strip the "<Provider>-<Type>-HH:MM:SS-YYYY-MM-DD-" prefix, e.g.
    // "SoilLabs-Raw-11:56:25-2026-03-30-Agro CWDig - FR.csv" → "Agro CWDig - FR.csv"
    const match = title.match(
      /^[^-]+-[^-]+-\d{2}:\d{2}:\d{2}-\d{4}-\d{2}-\d{2}-(.+)$/
    );
    return match?.[1]?.trim() ?? title.trim();
  };

  const renderStatsPanel = (stats: RasterStats | null): React.ReactNode => {
    if (!stats) return null;
  };

  const handleThresholdChange = (
    propertyId: string,
    newThresholds: number[]
  ): void => {
    const config =
      chartConfigsRef.current[propertyId] ?? chartConfigs[propertyId];
    const targetZones = config?.pendingZones ?? config?.zones ?? 4;
    const stats = config?.distributionStats ?? config?.stats;
    const sortedThresholds = stats
      ? sanitizeThresholds(newThresholds, stats.min, stats.max, targetZones)
      : [...newThresholds].sort((a, b) => a - b);
    setChartConfigs((prev) => {
      const next = {
        ...prev,
        [propertyId]: {
          ...prev[propertyId],
          pendingThresholds: sortedThresholds,
          thresholds: sortedThresholds,
        },
      };
      chartConfigsRef.current = next;
      return next;
    });
    setPendingChanges((prev) => ({ ...prev, [propertyId]: true }));
  };

  const renderCollectionContent = (collection: Collection): React.ReactNode => {
    if (loadingCollectionData) {
      return <Spin tip="Loading collection data..." />;
    }

    if (!selectedCollection || selectedCollection?.id !== collection?.id) {
      return <Text>No data available for this collection</Text>;
    }

    if (
      !selectedCollection?.layer_properties ||
      selectedCollection?.layer_properties?.length === 0
    ) {
      return <Text>No soil properties available for this collection</Text>;
    }

    return (
      <Collapse
        className="soil-tests-accordion"
        bordered={false}
        activeKey={expandedProperties}
        onChange={handlePropertyPanelChange}
        accordion
      >
        {selectedCollection?.layer_properties.map((property) => {
          const config = chartConfigs[property.id] || {};
          const thresholds = config.thresholds ?? [];
          const effectiveZones = config.pendingZones ?? config.zones ?? 4;
          const distributionStats = config.distributionStats ?? config.stats;
          const distributionZoneData =
            config.distributionZoneData ?? config.zoneData ?? [];

          return (
            <Panel
              header={
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ width: "100%" }}
                  onClick={() => {
                    handlePropertyHeaderClick(property, config, false);
                  }}
                >
                  <Text strong>{property?.name}</Text>
                  <Flex align="center" gap={8}>
                    <Button
                      size="small"
                      icon={
                        expandedProperties.includes(property.id) ? (
                          <UpOutlined />
                        ) : (
                          <DownOutlined />
                        )
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePropertyHeaderClick(property, config, true);
                      }}
                    >
                      {config?.chartData ??
                      expandedProperties?.includes(property.id)
                        ? ""
                        : "Load"}
                    </Button>
                  </Flex>
                </Flex>
              }
              key={property.id}
              className="soil-test-panel"
              showArrow={false}
            >
              <div className="soil-test-content">
                {config.loading ? (
                  <div style={{ textAlign: "center", padding: "40px" }}>
                    <Spin tip="Loading soil data..." size="large" />
                  </div>
                ) : config.chartData && config.stats ? (
                  <>
                    <SoilAnalysisChart
                      zoneData={config.zoneData}
                      zoneThresholds={thresholds}
                      propertyName={property.name}
                      uom={property.uom}
                      distributionStats={distributionStats}
                      palette={config.palette}
                      distributionZoneData={distributionZoneData}
                    />

                    {distributionStats && distributionZoneData.length > 0 && (
                      <ZoneColorSlider
                        key={`${property.id}-${effectiveZones}-${thresholds?.length}`}
                        zones={effectiveZones}
                        palette={config.palette || "default"}
                        distributionStats={distributionStats}
                        distributionZoneData={distributionZoneData}
                        thresholds={thresholds}
                        propertyName={property.name}
                        onThresholdChange={(newThresholds) => {
                          handleThresholdChange(property.id, newThresholds);
                        }}
                      />
                    )}
                    {renderStatsPanel(config.stats)}

                    <div className="controls-container">
                      <div className="control-group">
                        <Text strong>Number of zones</Text>
                        <Select
                          value={config.pendingZones ?? config.zones}
                          style={{ width: "100%" }}
                          onChange={(value) => {
                            handleZonesChange(property.id, value);
                          }}
                        >
                          {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                            <Option key={num} value={num}>
                              {num}
                            </Option>
                          ))}
                        </Select>
                      </div>

                      <div className="control-group">
                        <Text strong>Color palette</Text>
                        <Select
                          value={config.pendingPalette ?? config.palette}
                          style={{ width: "100%" }}
                          onChange={(value) => {
                            handlePaletteChange(property.id, value);
                          }}
                        >
                          <Option value="default">Default</Option>
                          <Option value="accessibility">Accessibility</Option>
                        </Select>
                      </div>

                      <Button
                        type="primary"
                        className="apply-button"
                        disabled={!pendingChanges[property.id]}
                        onClick={() => {
                          void handleApplyChanges(property.id);
                        }}
                      >
                        Apply
                      </Button>
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      color: "#999",
                    }}
                  >
                    <Text>Click the Load button to load soil data</Text>
                  </div>
                )}
              </div>
            </Panel>
          );
        })}
      </Collapse>
    );
  };

  let drawerContent: React.ReactNode;

  if (loadingCollections && collections.length === 0) {
    drawerContent = (
      <div className="drawer-content-state">
        <Spin tip="Loading collections..." />
      </div>
    );
  } else if (!loadingCollections && collections.length === 0) {
    drawerContent = (
      <div className="drawer-content-state">
        No soil test collections available
      </div>
    );
  } else {
    drawerContent = (
      <>
        <div className="collections-scroll" onScroll={handleCollectionsScroll}>
          <Collapse
            className="collections-accordion"
            bordered={false}
            activeKey={expandedCollections}
            onChange={handleCollectionPanelChange}
            expandIconPosition="end"
            accordion
          >
            {collections?.map((collection) => (
              <Panel
                header={
                  <Flex justify="space-between" align="center">
                    <Flex vertical>
                      <Text type="secondary">
                        {new Date(collection.created).toLocaleDateString()}
                      </Text>
                      <Text strong>
                        {trimCollectionTitle(collection.title)}
                      </Text>
                    </Flex>
                  </Flex>
                }
                key={collection.id}
                className="collection-panel"
              >
                {renderCollectionContent(collection)}
              </Panel>
            ))}
          </Collapse>
        </div>
        {loadingMoreCollections && (
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Spin size="small" />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="soil-analysis-drawer">
      {isLoading && <Loader mask={true} />}
      <Flex justify="space-between" align="center" className="drawer-header">
        <Text strong className="drawer-title">
          Soil analysis maps <InfoCircleOutlined />
        </Text>
        {onCloseDrawer && (
          <Button
            type="text"
            className="drawer-close-button"
            aria-label="Close soil analysis drawer"
            icon={<CloseOutlined />}
            onClick={onCloseDrawer}
          />
        )}
      </Flex>
      {drawerContent}
    </div>
  );
};

export default SoilAnalysisDrawer;
